<template>
  <v-container class="pt-0">
    <v-form ref="form">
      <v-row justify="center">
        <v-col md="8" xs="12">
          <v-card
            v-if="demo"
            :flat="!$vuetify.breakpoint.smAndUp"
            class="mb-3"
            color="primary"
            tile
          >
            <v-card-title style="color: white"> This is a demo. </v-card-title>
            <v-card-text style="color: white">
              Demo links cannot be used for real jobs.
            </v-card-text>
          </v-card>

          <v-card
            v-if="artLink"
            :flat="!$vuetify.breakpoint.smAndUp"
            class="mb-3"
            color="accent"
            tile
          >
            <v-card-title style="color: white">
              This is an art department timecard link.
            </v-card-title>
            <v-card-text style="color: white">
              Your submission will be sent to the art coordinator.
            </v-card-text>
          </v-card>

          <job-submission-info :job="submission" :art-link="artLink" />

          <v-card :flat="!$vuetify.breakpoint.smAndUp" class="my-3">
            <v-card-title :class="!$vuetify.breakpoint.smAndUp ? 'pa-0' : ''">
              Your Information
            </v-card-title>
            <v-card-text :class="!$vuetify.breakpoint.smAndUp ? 'pa-0' : ''">
              <v-row justify="center" align="center">
                <v-col md="3" cols="6" class="py-0">
                  <v-text-field
                    v-model="submission.employee.firstName"
                    label="First Name*"
                    :rules="[rules.required]"
                    type="text"
                    data-cy="caps-text-first-name"
                  />
                </v-col>
                <v-col md="3" cols="6" class="py-0">
                  <v-text-field
                    v-model="submission.employee.middleName"
                    label="Middle Name"
                    type="text"
                    data-cy="caps-text-middle-name"
                  />
                </v-col>
                <v-col md="6" cols="12" class="py-0">
                  <v-text-field
                    v-model="submission.employee.lastName"
                    label="Last Name*"
                    :rules="[rules.required]"
                    type="text"
                    data-cy="caps-text-last-name"
                  />
                </v-col>
                <v-col cols="8" class="py-0">
                  <v-text-field
                    v-model="submission.employee.telephone"
                    v-mask="masks.telePhone"
                    :rules="[rules.required]"
                    type="tel"
                    label="Phone*"
                    data-cy="caps-text-number"
                  />
                </v-col>
                <v-col cols="4" class="py-0">
                  <v-select
                    v-model="submission.employee.gender"
                    :items="genders"
                    label="Gender"
                    data-cy="gender-input"
                  />
                </v-col>
                <v-col cols="12" class="py-0">
                  <v-text-field
                    v-model="submission.employee.email"
                    label="Email*"
                    :rules="[rules.required, rules.email]"
                    type="email"
                    data-cy="caps-text-email"
                  />
                </v-col>
                <v-col cols="8" class="py-0">
                  <v-text-field
                    v-model="submission.employee.socialSecurityNumber"
                    v-mask="masks.SSN"
                    masked
                    label="Social Security Number*"
                    :rules="[rules.required]"
                    pattern="\d*"
                    data-cy="caps-text-ssn"
                  />
                </v-col>
                <v-col cols="4" class="py-0">
                  <v-select
                    v-if="isCaliforniaMarket && !job.isNonUnion"
                    v-model="submission.employee.union"
                    :items="unions"
                    item-text="union"
                    item-value="union"
                    label="Union*"
                    v-on:change="selectUnionWageTheft"
                    :rules="[rules.required]"
                    data-cy="caps-select-union"
                  />
                  <v-text-field
                    v-else-if="job.isNonUnion"
                    v-model="submission.employee.union"
                    value="NON"
                    label="NON Union"
                    disabled
                    data-cy="caps-text-union"
                  />
                  <v-text-field
                    v-else
                    v-model="submission.employee.union"
                    label="Union*"
                    v-on:change="selectUnionWageTheft"
                    :rules="[rules.required]"
                    data-cy="caps-text-union"
                  />
                </v-col>
                <v-col cols="12" class="py-0">
                  <v-text-field
                    ref="occupation"
                    v-model="submission.employee.occupation"
                    label="Occupation/Position*"
                    :rules="[rules.required]"
                    data-cy="caps-text-occupation"
                  />
                </v-col>
                <v-col cols="12" md="6" class="py-0">
                  <v-text-field
                    v-model="submission.employee.loanOut"
                    label="Loan Out (Business Name)"
                    :rules="
                      submission.includeStartingPaperwork &&
                      submission.employee.isIncorporated
                        ? [rules.required]
                        : []
                    "
                    data-cy="caps-text-loan-out"
                  />
                </v-col>
                <v-col cols="12" md="6" class="py-0">
                  <v-text-field
                    v-model="submission.employee.federalId"
                    v-mask="masks.EIN"
                    label="Federal Id (EIN)"
                    :rules="
                      submission.includeStartingPaperwork &&
                      submission.employee.isIncorporated
                        ? [rules.required]
                        : []
                    "
                    data-cy="caps-text-federal-id"
                  />
                </v-col>

                <v-col cols="6" class="py-0">
                  <v-text-field
                    v-model="submission.employee.rate"
                    label="Rate"
                    type="number"
                    prefix="$"
                    data-cy="caps-text-rate"
                  />
                </v-col>
                <v-col cols="6" class="py-0">
                  <v-select
                    v-model="submission.employee.rateType"
                    :items="rateTypes"
                    :rules="[rules.required]"
                    label="Rate Type"
                    data-cy="caps-select-rate-type"
                  />
                </v-col>
                <v-expand-transition>
                  <v-col
                    v-if="submission.employee.rateType === 'Other'"
                    cols="12"
                    class="py-0"
                  >
                    <v-text-field
                      v-model="submission.employee.rateTypeOther"
                      label="Rate Type (Other)"
                    />
                  </v-col>
                </v-expand-transition>
              </v-row>
            </v-card-text>
          </v-card>

          <v-card :flat="!$vuetify.breakpoint.smAndUp">
            <v-card-title :class="!$vuetify.breakpoint.smAndUp ? 'pa-0' : ''">
              Start Paperwork
            </v-card-title>
            <v-card-text :class="!$vuetify.breakpoint.smAndUp ? 'pa-0' : ''">
              <v-row wrap>
                <v-col cols="12" class="py-0">
                  <v-checkbox
                    id="caps-form-start-paperwork-checkbox"
                    v-model="submission.includeStartingPaperwork"
                    label="Include start paperwork (This is my first week of work)"
                    data-cy="caps-form-start-paperwork-checkbox"
                  />
                </v-col>
              </v-row>

              <v-expand-transition>
                <v-row v-if="submission.includeStartingPaperwork" wrap>
                  <v-col cols="12" class="py-0">
                    <DatePickerDialog
                      year-first
                      v-model="submission.employee.birthday"
                      label="Date of Birth"
                      birthday
                      :rules="[rules.required]"
                      data-cy="caps-date-picker-dob"
                    />
                  </v-col>

                  <v-col cols="12" md="12" class="py-0">
                    <h4>Personal Address</h4>
                  </v-col>

                  <v-col cols="12" class="py-0">
                    <v-text-field
                      v-model="submission.employee.address.streetName"
                      label="Street Name*"
                      :rules="[rules.required]"
                      data-cy="caps-text-personal-street-name"
                    />
                  </v-col>
                  <v-col cols="12" class="py-0">
                    <v-text-field
                      v-model="submission.employee.address.streetName2"
                      label="Street Name Line Two (Optional)"
                      data-cy="caps-text-personal-street-name-2"
                    />
                  </v-col>
                  <v-col cols="6" md="6" class="py-0">
                    <v-text-field
                      v-model="submission.employee.address.city"
                      label="City*"
                      :rules="[rules.required]"
                      data-cy="caps-text-personal-city"
                    />
                  </v-col>
                  <v-col cols="3" md="3" class="py-0">
                    <v-text-field
                      v-model="submission.employee.address.state"
                      v-mask="masks.stateAbbrv"
                      label="State*"
                      :rules="[rules.required]"
                      data-cy="caps-text-personal-state"
                    />
                  </v-col>
                  <v-col cols="3" md="3" class="py-0">
                    <v-text-field
                      v-model="submission.employee.address.zipCode"
                      label="Zip Code*"
                      :rules="[rules.required]"
                      data-cy="caps-text-personal-zip"
                    />
                  </v-col>

                  <v-col cols="12" class="py-0">
                    <v-checkbox
                      key="0"
                      v-model="submission.employee.isIncorporated"
                      data-cy="submission-form-checkbox-incorporated"
                      label="I am incorporated and do not need to fill out a W4."
                    />
                  </v-col>

                  <v-expand-transition>
                    <v-col v-if="!submission.employee.isIncorporated" cols="12">
                      <v-row>
                        <v-col cols="12">
                          <h3>W-4</h3>
                        </v-col>
                        <v-col cols="6" sm="3" class="py-0">
                          <v-select
                            v-model="submission.w4.maritalStatus"
                            :items="maritalStatuses"
                            :rules="[rules.required]"
                            label="Marital Status"
                            data-cy="caps-select-marital-status"
                          />
                        </v-col>
                      </v-row>
                      <v-row>
                        <v-col
                          v-if="!submission.exemptFromWithholdings"
                          cols="6"
                          class="py-0"
                        >
                          <v-tooltip top>
                            <template v-slot:activator="{ on, attrs }">
                              <v-text-field
                                v-model="submission.w4.amountDependents"
                                v-bind="attrs"
                                label="Withholdings based on dependents"
                                prefix="$"
                                :rules="[rules.numeric]"
                                data-cy="caps-text-Withholdings"
                              >
                                <template v-slot:append>
                                  <div v-bind="attrs" v-on="on">(?)</div>
                                </template>
                              </v-text-field>
                            </template>
                            <img
                              style="width: 100%"
                              :src="JobSubmissionW4WithholdingsTooltip"
                            />
                          </v-tooltip>
                        </v-col>
                        <v-col cols="6" class="py-0">
                          <v-checkbox
                            key="0"
                            v-model="submission.exemptFromWithholdings"
                            label="I'm Exempt"
                          />
                        </v-col>
                        <v-col
                          v-if="!submission.exemptFromWithholdings"
                          cols="6"
                          class="py-0"
                        >
                          <v-tooltip top>
                            <template v-slot:activator="{ on, attrs }">
                              <v-text-field
                                v-model="submission.w4.extraWithholdings"
                                v-bind="attrs"
                                label="Extra Withholdings (Optional)"
                                prefix="$"
                                :rules="[rules.numeric]"
                                data-cy="caps-text-Withholdings"
                              >
                                <template v-slot:append>
                                  <div v-bind="attrs" v-on="on">(?)</div>
                                </template>
                              </v-text-field>
                            </template>
                            <img
                              style="width: 100%"
                              :src="JobSubmissionW4ExtraWithholdingsToolTip"
                            />
                          </v-tooltip>
                        </v-col>
                      </v-row>
                    </v-col>
                  </v-expand-transition>

                  <v-expand-transition>
                    <v-col
                      v-if="submission.employee.isIncorporated"
                      class="py-0"
                    >
                      <job-submission-w9
                        v-model="submission"
                        @input-w9-upload="
                          (val) => {
                            w9Upload = val;
                          }
                        "
                        @input-article-of-incorporation="
                          (val) => {
                            articleOfIncorporationUpload = val;
                          }
                        "
                      />
                    </v-col>
                  </v-expand-transition>

                  <v-col cols="12">
                    <v-row wrap no-gutters>
                      <v-col cols="12" class="pt-4">
                        <h3>I-9</h3>
                      </v-col>
                      <v-col cols="12" md="12" class="py-0">
                        <v-select
                          v-model="submission.employee.citizenshipStatus"
                          :items="citizenshipStatuses"
                          :rules="[rules.required]"
                          label="Citizenship Status*"
                          data-cy="caps-select-citizenship-status"
                        />
                      </v-col>
                      <v-expand-transition>
                        <v-col
                          v-if="isAlienAuthorizedToWork"
                          cols="12"
                          class="py-0"
                        >
                          <v-checkbox
                            key="0"
                            v-model="
                              submission.employee.workAuthorization.expires
                            "
                            label="Work Authorization Expires"
                          />
                        </v-col>
                      </v-expand-transition>
                      <v-expand-transition>
                        <v-col
                          v-if="workAuthorizationExpires"
                          cols="12"
                          class="py-0"
                        >
                          <date-picker-dialog
                            v-model="
                              submission.employee.workAuthorization
                                .expirationDate
                            "
                            label="Expiration Date*"
                            year-first
                            future-only
                          />
                        </v-col>
                      </v-expand-transition>
                      <v-expand-transition>
                        <v-col
                          v-if="isAlienAuthorizedToWork"
                          cols="12"
                          md="12"
                          class="py-0"
                        >
                          <v-select
                            v-model="submission.employee.workAuthorization.type"
                            :items="workAuthorizationTypes"
                            :rules="[rules.required]"
                            label="Work Authorization Type*"
                          />
                        </v-col>
                      </v-expand-transition>
                      <v-expand-transition>
                        <v-col
                          v-if="alienRegistrationUSCISNumberAuthorization"
                          cols="12"
                          md="12"
                          class="py-0"
                        >
                          <v-text-field
                            v-model="
                              submission.employee.workAuthorization
                                .alienRegistrationUSCISNumber
                            "
                            label="Alien Registration/USCIS Number"
                            :rules="[rules.required]"
                          />
                        </v-col>
                        <v-col
                          v-else-if="formI94AdmissionNumberAuthorization"
                          cols="12"
                          md="12"
                          class="py-0"
                        >
                          <v-text-field
                            v-model="
                              submission.employee.workAuthorization
                                .formI94AdmissionNumber
                            "
                            label="Form I-94 Admission Number*"
                            :rules="[rules.required]"
                          />
                        </v-col>
                        <v-col
                          v-if="foreignPassportAuthorization"
                          cols="12"
                          md="12"
                          class="py-0"
                        >
                          <v-text-field
                            v-model="submission.employee.passport.number"
                            label="Passport Number*"
                            :rules="[rules.required]"
                          />
                        </v-col>
                      </v-expand-transition>

                      <v-col cols="12" md="12" class="py-0">
                        <v-select
                          v-model="submission.i9.documentType"
                          :items="i9DocumentTypes"
                          :rules="[rules.required]"
                          label="Document Type*"
                          data-cy="caps-select-document-type"
                        />
                      </v-col>

                      <v-col
                        v-if="submission.i9.documentType === 'passport'"
                        cols="12"
                      >
                        <v-row no-gutters>
                          <v-col cols="12">
                            <v-text-field
                              v-model="
                                submission.i9.documents.passport
                                  .issuingAuthority
                              "
                              :rules="[rules.required]"
                              label="Issuing Authority*"
                            />
                          </v-col>
                          <v-col cols="12">
                            <v-text-field
                              v-model="submission.i9.documents.passport.number"
                              :rules="[rules.required]"
                              label="Document Number*"
                              data-cy="caps-text-document-number"
                            />
                          </v-col>
                          <v-col cols="12">
                            <date-picker-dialog
                              v-model="
                                submission.i9.documents.passport.expirationDate
                              "
                              label="Expiration Date*"
                              year-first
                              future-only
                              data-cy="caps-date-expiration-date"
                            />
                          </v-col>
                        </v-row>
                      </v-col>

                      <v-col
                        v-if="submission.i9.documentType === 'driversLicense'"
                        cols="12"
                      >
                        <v-row no-gutters>
                          <v-col cols="12">
                            <v-text-field
                              v-model="
                                submission.i9.documents.driversLicense
                                  .issuingAuthority
                              "
                              :rules="[rules.required]"
                              label="State"
                            />
                          </v-col>
                          <v-col cols="12">
                            <v-text-field
                              v-model="
                                submission.i9.documents.driversLicense.number
                              "
                              :rules="[rules.required]"
                              label="Document Number*"
                            />
                          </v-col>
                          <v-col cols="12">
                            <date-picker-dialog
                              v-model="
                                submission.i9.documents.driversLicense
                                  .expirationDate
                              "
                              label="Expiration Date*"
                              year-first
                              future-only
                            />
                          </v-col>
                        </v-row>
                      </v-col>

                      <v-col
                        v-if="submission.i9.documentType === 'other'"
                        cols="12"
                      >
                        <v-row no-gutters>
                          <v-col cols="12" md="12" class="py-0">
                            <v-select
                              v-model="submission.i9.otherDocumentListCategory"
                              :items="['List A', 'List B']"
                              label="Document List*"
                              :rules="[rules.required]"
                            />
                          </v-col>
                          <v-col cols="12">
                            <v-text-field
                              v-model="submission.i9.documents.other.title"
                              :rules="[rules.required]"
                              label="Document Title*"
                            />
                          </v-col>
                          <v-col cols="12">
                            <v-text-field
                              v-model="
                                submission.i9.documents.other.issuingAuthority
                              "
                              :rules="[rules.required]"
                              label="Issuing Authority*"
                            />
                          </v-col>
                          <v-col cols="12">
                            <v-text-field
                              v-model="submission.i9.documents.other.number"
                              :rules="[rules.required]"
                              label="Document Number*"
                            />
                          </v-col>
                          <v-col cols="12">
                            <v-checkbox
                              v-model="submission.i9.documents.other.expires"
                              label="Expires"
                            />
                          </v-col>
                          <v-col
                            v-if="submission.i9.documents.other.expires"
                            cols="12"
                          >
                            <date-picker-dialog
                              v-model="
                                submission.i9.documents.other.expirationDate
                              "
                              label="Expiration Date*"
                              year-first
                              future-only
                            />
                          </v-col>
                        </v-row>
                      </v-col>

                      <v-expand-transition>
                        <v-col
                          v-if="foreignPassportAuthorization"
                          cols="12"
                          md="12"
                          class="py-0"
                        >
                          <v-text-field
                            v-model="
                              submission.employee.passport.issuingAuthority
                            "
                            :rules="[rules.required]"
                            label="Country of Issuance*"
                          />
                        </v-col>
                      </v-expand-transition>
                    </v-row>
                  </v-col>
                  <v-col v-if="hasEmployeeStartForm" cols="6" sm="6">
                    <h3>Ethnicity Selection</h3>
                    <v-select
                      v-model="submission.employee.ethnicity"
                      :items="ethnicities"
                      label="Ethnicity (Optional)"
                    ></v-select>
                    <v-text-field
                      v-if="submission.employee.ethnicity == 'Other'"
                      v-model="submission.employee.otherEthnicity"
                      :rules="[rules.required]"
                      label="Other Ethnicity*"
                    />
                  </v-col>
                  <v-col
                    v-if="
                      job.payrollProvider.id != 3 &&
                      job.payrollProvider.id != 5 &&
                      job.isDe4Included &&
                      !submission.employee.isIncorporated
                    "
                    cols="12"
                    sm="12"
                  >
                    <h3>California de4 Form</h3>
                    <v-row v-if="submission.employee.isIncorporated">
                      <v-col cols="9" sm="6" class="py-0">
                        <v-select
                          v-model="submission.w4.maritalStatus"
                          :items="maritalStatuses"
                          :rules="[rules.required]"
                          label="Marital Status"
                          data-cy="caps-select-marital-status"
                        />
                      </v-col>
                    </v-row>
                    <v-row>
                      <v-col cols="12" md="12" class="py-0">
                        <v-select
                          v-model="submission.de4.de4Selection"
                          :items="de4Choices"
                          :rules="[rules.required]"
                          label="Choose which part of the de4 form applies to you*"
                          data-cy="de4-select-part"
                        />
                      </v-col>
                    </v-row>
                    <v-row>
                      <v-col
                        v-if="submission.de4.de4Selection == 'choiceOne'"
                        cols="9"
                        class="py-0"
                      >
                        <v-tooltip top>
                          <template v-slot:activator="{ on, attrs }">
                            <v-text-field
                              v-model="
                                submission.de4
                                  .allowancesForRegularWithholdingAllowances
                              "
                              v-bind="attrs"
                              label="Number of Regular Withholding Allowances"
                              :rules="[rules.numeric, rules.required]"
                              data-cy="caps-text-Withholdings"
                            >
                              <template v-slot:append>
                                <div v-bind="attrs" v-on="on">(?)</div>
                              </template>
                            </v-text-field>
                          </template>
                          <img
                            style="width: 100%"
                            :src="
                              JobSubmissionDe4WorksheetAPartOneWithholdingsTooltip
                            "
                          />
                        </v-tooltip>
                      </v-col>
                    </v-row>
                    <v-row>
                      <v-col
                        v-if="submission.de4.de4Selection == 'choiceOne'"
                        cols="9"
                        class="py-0"
                      >
                        <v-tooltip top>
                          <template v-slot:activator="{ on, attrs }">
                            <v-text-field
                              v-model="
                                submission.de4.allowancesFromEstimatedDeductions
                              "
                              v-bind="attrs"
                              label="Number of allowances from the Estimated Deductions (if applicable)"
                              :rules="[rules.numeric, rules.required]"
                              data-cy="de4-text-allowences"
                            >
                              <template v-slot:append>
                                <div v-bind="attrs" v-on="on">(?)</div>
                              </template>
                            </v-text-field>
                          </template>
                          <img
                            style="width: 100%"
                            :src="
                              JobSubmissionDe4WorksheetAPartTwoWithholdingsTooltip
                            "
                          />
                        </v-tooltip>
                      </v-col>
                    </v-row>
                    <v-row>
                      <v-col
                        v-if="submission.de4.de4Selection == 'choiceOne'"
                        cols="12"
                        class="py-0"
                      >
                        <v-tooltip top>
                          <template v-slot:activator="{ on, attrs }">
                            <v-text-field
                              v-model="
                                submission.de4.additionalAmountOfAllowances
                              "
                              v-bind="attrs"
                              prefix="$"
                              label="Additional amount, if any, you want withheld each pay period (if employer agrees)"
                              :rules="[rules.numeric, rules.required]"
                              data-cy="de4-text-additional-amount-of-state-income-tax"
                            >
                              <template v-slot:append>
                                <div v-bind="attrs" v-on="on">(?)</div>
                              </template>
                            </v-text-field>
                          </template>
                          <img
                            style="width: 100%"
                            :src="JobSubmissionDe4WorksheetCWithholdingsTooltip"
                          />
                        </v-tooltip>
                      </v-col>
                    </v-row>
                    <v-col
                      cols="12"
                      class="py-0"
                      v-if="submission.de4.de4Selection == 'choiceTwo'"
                    >
                      <v-radio-group
                        v-model="submission.de4.exemptionFromWitholding"
                        mandatory
                      >
                        <v-radio
                          label="I claim exemption from withholding for 2022, and I certify I meet both of the conditions for exemption."
                          value="radio-1"
                        ></v-radio>
                        <v-col
                          v-if="submission.de4.de4Selection == 'choiceTwo'"
                          cols="12"
                          class="py-0"
                        >
                          <h3>OR</h3>
                        </v-col>
                        <v-radio
                          label="I certify under penalty of perjury that I am not subject to California withholding. I meet the conditions set
forth under the Service Member Civil Relief Act, as amended by the Military Spouses Residency Relief Act
and the Veterans Benefits and Transition Act of 2018."
                          value="radio-2"
                        ></v-radio>
                      </v-radio-group>
                    </v-col>
                  </v-col>
                </v-row>
              </v-expand-transition>
            </v-card-text>
          </v-card>

          <v-card class="my-3" :flat="!$vuetify.breakpoint.smAndUp">
            <v-card-title :class="!$vuetify.breakpoint.smAndUp ? 'pa-0' : ''">
              Work Week and Hours
            </v-card-title>
            <v-card-text :class="!$vuetify.breakpoint.smAndUp ? 'pa-0' : ''">
              <v-row wrap no-gutters align="center" class="pt-2">
                <v-col cols="4" sm="3" style="font-size: 20px">
                  Week worked:
                </v-col>
                <v-col cols="8" sm="9">
                  <SundayDatePickerDialog
                    v-model="submission.date"
                    :job="job"
                    data-cy="caps-date-picker-week-of"
                  />
                </v-col>
                <v-col cols="12">
                  <v-checkbox
                    v-model="submission.employee.isManager"
                    label="I am Exempt"
                  />
                </v-col>
              </v-row>
              <div
                v-for="(item, index) in submission.times"
                :key="index"
                style="border-bottom: 3px solid #a4a4a4"
              >
                <div :disabled="!item.workedToday">
                  <v-row
                    class="px-0"
                    justify="space-between"
                    align="center"
                    no-gutters
                    wrap
                  >
                    <v-col
                      style="font-size: 18px"
                      class="mt-1"
                      cols="12"
                      sm="4"
                    >
                      <span v-if="!item.workedToday">
                        {{ item.dayOfWeek }}
                      </span>
                      <b v-else>
                        {{ item.dayOfWeek }}
                      </b>
                    </v-col>
                    <v-col cols="auto" class="px-0 mt-0">
                      <v-checkbox
                        key="0"
                        v-model="item.workedToday"
                        style="width: 160px"
                        label="I worked this day"
                        :data-cy="`caps-time-checkbox-${index}`"
                      />
                    </v-col>
                  </v-row>
                  <v-row justify="end">
                    <v-col cols="auto" class="px-0 mt-0">
                      <div>
                        Copy Last Day
                        <v-btn icon @click="copyLastDay(item)">
                          <v-icon> mdi-clipboard-file </v-icon>
                        </v-btn>
                      </div>
                    </v-col>
                  </v-row>
                </div>
                <v-expand-transition>
                  <div v-if="item.workedToday">
                    <v-row
                      justify="space-between"
                      align="center"
                      no-gutters
                      class="ma-0 pa-0"
                      wrap
                    >
                      <v-col cols="2" class="pb-0">
                        <div class="pb-0">
                          <span style="font-size: 16px"> Date</span><br />
                          <span
                            :data-cy="`caps-time-text-date-${index}`"
                            style="color: black; font-size: 18px"
                          >
                            {{ date | dayOfWeekFilter(index) }}
                          </span>
                        </div>
                      </v-col>
                      <v-col cols="2" class="mr-3 pt-2">
                        <v-checkbox
                          v-model="item.hasNdb"
                          label="NDB"
                          :data-cy="`caps-has-ndb-${index}`"
                        >
                        </v-checkbox>
                      </v-col>
                      <v-col cols="5" md="5" class="mt-2 pb-0 pl-0">
                        <v-text-field
                          v-model="item.zipCode"
                          type="number"
                          pattern="\d*"
                          :rules="[rules.required, rules.number]"
                          label="Zip Code"
                          :data-cy="`caps-time-text-zip-${index}`"
                        />
                      </v-col>

                      <v-col v-if="job.hasOutOfZone" cols="12">
                        <v-text-field
                          v-model="item.outOfZoneTime"
                          label="Travel Time"
                          :rules="[rules.number]"
                          :data-cy="`caps-time-text-out-of-zone-${index}`"
                        />
                      </v-col>
                      <v-col v-if="job.hasTravel" cols="12">
                        <v-select
                          v-model="item.dayType"
                          :items="dayTypes"
                          label="Day Type"
                          :rules="[rules.required]"
                        />
                      </v-col>
                      <v-col
                        v-if="
                          !submission.employee.isManager &&
                          (!job.hasTravel || item.dayType === 'normal')
                        "
                        cols="12"
                        class="py-0"
                      >
                        <v-row no-gutters>
                          <v-col cols="3" md="3" class="pb-0 pr-2">
                            <TimePickerDialog
                              v-model="item.startTime"
                              label="Start"
                              :time-increment="job.timeIncrement"
                              :data-cy="`caps-time-picker-start-${index}`"
                            />
                          </v-col>
                          <v-col v-if="item.meals" cols="6" class="py-0 px-0">
                            <v-row wrap no-gutters>
                              <v-col
                                cols="12"
                                v-if="submission.employee.union != 'DGA'"
                              >
                                <v-row
                                  no-gutters
                                  align="center"
                                  justify="space-between"
                                >
                                  <v-col cols="6" style="padding-right: 10px">
                                    <TimePickerDialog
                                      v-model="item.meals[0].startTime"
                                      :label="`Out Meal #1`"
                                      :time-increment="job.timeIncrement"
                                      :data-cy="`caps-time-picker-meal-1-out-${index}`"
                                    />
                                  </v-col>
                                  <v-col cols="6" class="pr-2">
                                    <TimePickerDialog
                                      v-model="item.meals[0].endTime"
                                      :label="`In Meal #1`"
                                      :time-increment="job.timeIncrement"
                                      :data-cy="`caps-time-picker-meal-1-in-${index}`"
                                    />
                                  </v-col>
                                </v-row>
                              </v-col>
                              <v-col v-if="submission.employee.union != 'DGA'">
                                <v-expand-transition>
                                  <v-checkbox
                                    v-if="!item.recordSecondMeal"
                                    v-model="item.recordSecondMeal"
                                    :data-cy="`caps-checkbox-2nd-meal-${index}`"
                                    class="pl-1 pt-0 mt-0"
                                  >
                                    <template v-slot:label>
                                      <span style="font-size: 14px">
                                        Record second meal
                                      </span>
                                    </template>
                                  </v-checkbox>
                                </v-expand-transition>
                                <v-expand-transition>
                                  <v-row
                                    v-if="item.recordSecondMeal"
                                    no-gutters
                                    align="center"
                                    justify="space-between"
                                  >
                                    <v-col cols="6" style="padding-right: 10px">
                                      <TimePickerDialog
                                        v-model="item.meals[1].startTime"
                                        :label="`Out Meal #2`"
                                        :time-increment="job.timeIncrement"
                                        :data-cy="`caps-time-picker-meal-2-out-${index}`"
                                      />
                                    </v-col>
                                    <v-col cols="5" class="pr-2">
                                      <TimePickerDialog
                                        v-model="item.meals[1].endTime"
                                        :label="`In Meal #2`"
                                        :time-increment="job.timeIncrement"
                                        :data-cy="`caps-time-picker-meal-2-in-${index}`"
                                      />
                                    </v-col>
                                    <v-col cols="1">
                                      <v-icon
                                        style="
                                          margin-left: -10px;
                                          margin-bottom: 10px;
                                        "
                                        @click="item.recordSecondMeal = false"
                                      >
                                        mdi-close
                                      </v-icon>
                                    </v-col>
                                  </v-row>
                                </v-expand-transition>
                              </v-col>
                            </v-row>
                          </v-col>
                          <v-col cols="3" md="3" class="pb-0">
                            <TimePickerDialog
                              v-model="item.endTime"
                              label="End"
                              :time-increment="job.timeIncrement"
                              :data-cy="`caps-time-picker-end-${index}`"
                            />
                          </v-col>
                        </v-row>
                        <v-row justify="start" align="center">
                          <v-col cols="6">
                            <v-checkbox
                              v-model="item.hasRateChange"
                              label="Rate Change"
                            >
                            </v-checkbox>
                          </v-col>
                          <v-col cols="3" v-if="item.hasRateChange">
                            <v-text-field
                              v-model="item.rateChange"
                              label="Rate Change*"
                              type="number"
                              prefix="$"
                              :rules="[rules.required, rules.numeric]"
                            >
                            </v-text-field>
                          </v-col>
                          <v-col cols="3" v-if="item.hasRateChange">
                            / {{ submission.employee.rateType }}
                          </v-col>
                        </v-row>
                      </v-col>
                    </v-row>
                  </div>
                </v-expand-transition>
              </div>
            </v-card-text>
          </v-card>

          <job-submission-kit-mileage-rental
            v-if="job && doneLoading && job.showKitRental || job.showMileageForm"
            v-model="submission"
            :date="date"
          />
          <job-submission-freelancer-notes v-model="submission" />

          <v-card :flat="!$vuetify.breakpoint.smAndUp">
            <v-card-title :class="!$vuetify.breakpoint.smAndUp ? 'pa-0' : ''">
              Submit
            </v-card-title>
            <v-card-text :class="!$vuetify.breakpoint.smAndUp ? 'pa-0' : ''">
              <v-row wrap no-gutters justify="center">
                <v-col
                  cols="12"
                  class="pb-0 pt-4 mt-0"
                  style="text-align: center; font-size: 12px; line-height: 12px"
                >
                  <i
                    >The parties agree that this agreement may be electronically
                    signed. The parties agree that the electronic signatures
                    appearing on this agreement are the same as handwritten
                    signatures for the purposes of validity, enforceability and
                    admissibility.</i
                  >
                  <v-checkbox
                    v-model="consentToElectronicSignature"
                    :rules="[rules.required]"
                    label="I agree with the above and consent to doing business online."
                    data-cy="caps-checkbox-digital-business"
                  />
                  <v-checkbox
                    v-if="
                      job.isNdaIncluded && submission.includeStartingPaperwork
                    "
                    v-model="consentToNDA"
                    :rules="[rules.required]"
                    label="Agree to NDA"
                  />
                </v-col>
                <v-col cols="auto" class="pa-0">
                  Employee Signature
                  <SignaturePad ref="signaturePad" />
                </v-col>
                <v-col cols="12" class="pt-4" style="text-align: center">
                  An email will be sent to the address you listed
                  <b>
                    {{
                      submission.employee.email
                        ? `(${submission.employee.email})`
                        : ""
                    }}
                  </b>
                  containing a copy of these generated forms.
                </v-col>
              </v-row>
            </v-card-text>

            <v-card-actions>
              <v-row justify="center">
                <v-col cols="auto">
                  <JobSubmissionPreviewDialog
                    v-model="showPreviewDialog"
                    :pdf-src="pdfSrc"
                    :loading="sendingToProductionManager"
                    :processing-preview="processingPreview"
                    :num-pages="numPages"
                    @show-preview="showPreview()"
                    @submit="submit()"
                  />
                </v-col>
              </v-row>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>
    </v-form>
  </v-container>
</template>change

<style scoped lang="scss">
@import "@/scss/main";

.date-row {
  border-bottom: 1px solid $gray-light;
}

.wider-expansion {
  margin: -24px -24px;
}

.accordion-checkbox {
  padding-top: 22px;
  width: 205px;
}

.dropdown-cover {
  margin-right: -36px;
  z-index: 2;
  background-color: white;
}
</style>

<script>
import pdf from "vue-pdf";
import { DateTime } from "luxon";
import { mask } from "vue-the-mask";
import masks from "../utils/masks";
import { getUnions } from "../services/unions";
import SignaturePad from "../components/SignaturePad.vue";
import { generateForm, getJobFromHash } from "../services/jobs";
import TimePickerDialog from "../components/TimePickerDialog.vue";
import DatePickerDialog from "../components/DatePickerDialog.vue";
import SundayDatePickerDialog from "../components/SundayDatePickerDialog.vue";
import JobSubmissionW4WithholdingsTooltip from "../assets/JobSubmissionW4WithholdingsTooltip.png";
import JobSubmissionDe4WorksheetAPartOneWithholdingsTooltip from "../assets/de4_worksheet_a_part_1.png";
import JobSubmissionDe4WorksheetAPartTwoWithholdingsTooltip from "../assets/de4_worksheet_a_part_2.png";
import JobSubmissionDe4WorksheetCWithholdingsTooltip from "../assets/de4_worksheet_c.png";
import JobSubmissionKitMileageRental from "../components/JobSubmissionKitMileageRental.vue";
import JobSubmissionInfo from "../components/JobSubmissionInfo.vue";
import JobSubmissionFreelancerNotes from "../components/JobSubmissionFreelancerNotes.vue";
import JobSubmissionW9 from "../components/JobSubmissionW9.vue";
import JobSubmissionPreviewDialog from "../components/JobSubmissionPreviewDialog.vue";
import { CALIFORNIA_MARKET_KEY } from "../services/markets";

export default {
  filters: {
    dayOfWeekFilter(date, index) {
      return date
        ? DateTime.fromFormat(date, "yyyy-MM-dd")
            .plus({ days: index })
            .toFormat("MM/dd")
        : "--/--";
    },
  },
  components: {
    SignaturePad,
    TimePickerDialog,
    SundayDatePickerDialog,
    DatePickerDialog,
    JobSubmissionW9,
    JobSubmissionKitMileageRental,
    JobSubmissionFreelancerNotes,
    JobSubmissionInfo,
    JobSubmissionPreviewDialog,
  },
  directives: {
    mask,
  },
  props: {
    artLink: {
      type: Boolean,
    },
  },
  data() {
    return {
      unsavedDataCreated: false,
      CALIFORNIA_MARKET_KEY,
      settingUpPage: true,
      processingPreview: false,
      sendingToProductionManager: false,
      demo: false,
      consentToElectronicSignature: false,
      consentToNDA: false,
      JobSubmissionW4WithholdingsTooltip,
      JobSubmissionDe4WorksheetAPartOneWithholdingsTooltip,
      JobSubmissionDe4WorksheetAPartTwoWithholdingsTooltip,
      JobSubmissionDe4WorksheetCWithholdingsTooltip,
      w9Upload: null,
      articleOfIncorporationUpload: null,
      pdfSrc: null,
      numPages: null,
      openedPanel: [],
      page: 1,
      showPreviewDialog: false,
      ethnicities: [
        "Choose Not to Disclose",
        "White/Caucasian",
        "Asian",
        "African American",
        "Hispanic",
        "American Indian",
        "Other",
      ],
      genders: ["Male", "Female", "Other"],
      rateTypes: [
        {
          text: "Per Hour",
          value: "Hour",
        },
        {
          text: "For 8 Hours",
          value: "8hour",
        },
        {
          text: "For 10 Hours",
          value: "10hour",
        },
        {
          text: "For 12 Hours",
          value: "12hour",
        },
        "Day",
      ],
      dayTypes: [
        {
          text: "Normal",
          value: "normal",
        },
        {
          text: "Travel (Full)",
          value: "travel_full",
        },
        {
          text: "Travel (Half)",
          value: "travel_half",
        },
      ],
      unions: [],
      maritalStatuses: [
        {
          text: "Single or Married filing separately",
          value: "single",
        },
        {
          text: "Married filing jointly (or Qualifying widow(er))",
          value: "married",
        },
        {
          text: "Head of Household",
          value: "headOfHouse",
        },
      ],
      citizenshipStatuses: [
        {
          text: "Citizen",
          value: "citizen",
        },
        {
          text: "Non-Citizen National",
          value: "noncitizenNational",
        },
        {
          text: "Lawful Permanent Resident",
          value: "lawfulPermanentResident",
        },
        {
          text: "Alien Authorized to Work",
          value: "alienAuthorizedToWork",
        },
      ],
      de4Choices: [
        {
          text: "Witholding Allowences",
          value: "choiceOne",
        },
        {
          text: "Exemption From Witholding",
          value: "choiceTwo",
        },
      ],
      i9DocumentTypes: [
        {
          text: "Passport",
          value: "passport",
        },
        {
          text: "Drivers License",
          value: "driversLicense",
        },
        {
          text: "Other",
          value: "other",
        },
      ],
      workAuthorizationTypes: [
        {
          text: "Alien Registration/USCIS Number",
          value: "alienRegistrationUSCISNumber",
        },
        {
          text: "Form I-94 Admission Number",
          value: "formI94AdmissionNumber",
        },
        {
          text: "Foreign Passport",
          value: "foreignPassport",
        },
      ],
      openPDF: false,
      date: null,
      timeZone: null,
      masks,
      rules: {
        required: (value) => !!value || "Required.",
        counter: (value) => value.length <= 20 || "Max 20 characters",
        email: (value) => {
          const pattern =
            /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
          return pattern.test(value) || "Invalid e-mail.";
        },
        numeric: (value) => {
          if (!value) return true;
          if (/[a-zA-Z ]/.test(value)) return "Not a valid number.";
          return !Number.isNaN(parseFloat(value)) || "Not a valid number.";
        },
      },
      jobId: null,
      job: null,
      submission: {
        date: null,
        isPreview: true,
        sendEmployeeCopy: true,
        name: "",
        number: "",
        company: {
          employer: {
            firstName: "Nicole",
            lastName: "Holloway",
          },
          name: "",
          address: {
            streetName: "823 Seward Street",
            city: "Los Angeles",
            state: "CA",
            zipCode: "90038",
          },
        },
        employee: {
          firstName: "",
          middleName: "",
          lastName: "",
          socialSecurityNumber: "",
          address: {
            streetName: "",
            city: "",
            state: "",
            zipCode: "",
          },
          isUnion: true,
          email: "",
          union: "",
          birthday: "",
          occupation: "",
          ethnicity: "",
          otherEthnicity: "",
          loanOut: "",
          federalId: "",
          rateType: "Hour",
          passport: {},
          isManager: false,
          primaryLanguage: "",
          isEnglishPrimary: true,
          workAuthorization: {
            expires: false,
            expirationDate: DateTime.local().toFormat("yyyy-MM-dd"),
          },
        },
        de4: {
          allowancesForRegularWithholdingAllowances: "",
          allowancesFromEstimatedDeductions: "",
          additionalAmountOfAllowances: "",
          exemptionFromWitholding: "",
          notSubjectToCaliforniaWitholding: "",
          de4Selection: "",
        },
        w4: {
          maritalStatus: null,
          amountDependents: "",
          extraWithholdings: "",
        },
        w9: {
          upload: false,
          useSameAddress: false,
          businessName: "",
          address: {
            streetName: "",
            streetName2: "",
            city: "",
            state: "",
            zipCode: "",
          },
          uploadPDF: false,
        },
        i9: {
          documentType: null,
          documents: {
            A: [],
            B: [],
            C: [],
            other: {
              expires: false,
              title: "",
              issuingAuthority: "",
              number: "",
              expirationDate: "",
            },
            passport: {
              expires: true,
              title: "",
              issuingAuthority: "U.S. Dept. of State",
              number: "",
              expirationDate: "",
            },
            driversLicense: {
              expires: true,
              title: "",
              issuingAuthority: "CA",
              number: "",
              expirationDate: "",
            },
          },
        },
        kitRental: {
          addKitRental: false,
          uploadKitRentalInventory: false,
          inventoryList: [{}, {}],
        },
        mileageForm: {
          addMileageForm: false,
        },
        includeStartingPaperwork: true,
        exemptFromWithholdings: false,
        times: [
          {
            dayOfWeek: "Sunday",
          },
          {
            dayOfWeek: "Monday",
          },
          {
            dayOfWeek: "Tuesday",
          },
          {
            dayOfWeek: "Wednesday",
          },
          {
            dayOfWeek: "Thursday",
          },
          {
            dayOfWeek: "Friday",
          },
          {
            dayOfWeek: "Saturday",
          },
        ],
        freelancerNotes: "",
        uploads: [],
        hasEmployeeStartForm: false,
      },
    };
  },
  computed: {
    lawfulPermanentResident() {
      return (
        this.submission.employee.citizenshipStatus === "lawfulPermanentResident"
      );
    },
    isAlienAuthorizedToWork() {
      return (
        this.submission.employee.citizenshipStatus === "alienAuthorizedToWork"
      );
    },
    workAuthorization() {
      return this.submission.employee.workAuthorization;
    },
    workAuthorizationExpires() {
      return (
        this.isAlienAuthorizedToWork &&
        this.submission.employee.workAuthorization.expires
      );
    },
    alienRegistrationUSCISNumberAuthorization() {
      return (
        (this.isAlienAuthorizedToWork &&
          this.workAuthorization.type === "alienRegistrationUSCISNumber") ||
        this.lawfulPermanentResident
      );
    },
    formI94AdmissionNumberAuthorization() {
      return (
        this.isAlienAuthorizedToWork &&
        this.workAuthorization.type === "formI94AdmissionNumber"
      );
    },
    foreignPassportAuthorization() {
      return (
        this.isAlienAuthorizedToWork &&
        this.workAuthorization.type === "foreignPassport"
      );
    },
    occupations() {
      return this.unions.find(
        (union) => union.union === this.submission.employee.union
      )?.occupations;
    },
    isCaliforniaMarket() {
      return this.submission?.market?.key === "california";
    },
    isNewYorkMarket() {
      return this.submission?.market?.key === "new_york";
    },
  },
  watch: {
    date() {
      const date = DateTime.fromFormat(this.date, "yyyy-MM-dd").toLocal();
      this.submission.times.forEach((time, index) => {
        time.date = date.plus({ day: index }).toISO();
        time.start = DateTime.fromISO(
          `${date.plus({ day: index }).toISO().split("T")[0]}T${time.startTime}`
        ).toISO();
        time.end = DateTime.fromISO(
          `${date.plus({ day: index }).toISO().split("T")[0]}T${time.endTime}`
        ).toISO();
        time.meals.forEach((meal) => {
          meal.start = DateTime.fromISO(
            `${date.plus({ day: index }).toISO().split("T")[0]}T${
              meal.startTime
            }`
          ).toISO();
          meal.end = DateTime.fromISO(
            `${date.plus({ day: index }).toISO().split("T")[0]}T${meal.endTime}`
          ).toISO();
        });
      });
    },
    submission: {
      deep: true,
      handler(newVal, oldVal) {
        // Anytime the user inputs something it saves it to local storage so their submission
        // data won't be lost for that specific job even if they leave. Key is the hash and value is the submission object
        localStorage.setItem(this.job.hash, JSON.stringify(this.submission));
        this.date = this.submission.date;
        if (!this.settingUpPage) {
          this.unsavedDataCreated = true;
        }
      },
    },
    showPreviewDialog(val) {
      if (!val) this.pdfSrc = null;
    },
  },
  async mounted() {
    this.timeZone = DateTime.local().zoneName;
    this.demo = this.$route.query.demo === "true";
    this.bypassSignature = this.$route.query.bypassSignature === "true";
    if (this.$route.query.prefilled === "true") {
      this.submission.employee = {
        firstName: "Sam",
        middleName: "Bruce",
        lastName: "Crane",
        telephone: "1234567890",
        gender: "",
        socialSecurityNumber: "123456789",
        address: {
          streetName: "823 Seward Street",
          city: "Los Angeles",
          state: "CA",
          zipCode: "90038",
        },
        isUnion: true,
        isManager: false,
        email: "samcrane8@gmail.com",
        union: "44",
        occupation: "GUY",
        loanOut: "",
        federalId: "",
        birthday: "",
        citizenshipStatus: "citizen",
        rateType: "Hour",
        RateTypeOther: "",
        rate: 12,
        passport: {},
        driversLicense: {
          number: "",
          issuingAuthority: "",
        },
        workAuthorization: {
          expires: false,
          expirationDate: DateTime.local().toFormat("yyyy-MM-dd"),
        },
      };

      this.submission.w4.numChildren = 1;
      this.submission.w4.numOtherDependents = 1;
    }
    const date = DateTime.fromFormat("2011-12-18", "yyyy-MM-dd").toLocal();
    this.submission.times = this.submission.times.map((time, index) => ({
      ...time,
      workedToday: false,
      date: date.plus({ day: index }).toISO(),
      zipCode: null,
      start: "2011-12-19T15:28:46.493Z",
      startTime: "09:00",
      endTime: "18:00",
      end: "2011-12-19T15:28:46.493Z",
      recordSecondMeal: false,
      meals: [
        {
          startTime: "12:00",
          endTime: "13:00",
        },
        {
          startTime: "12:00",
          endTime: "13:00",
        },
      ],
      dayType: "normal",
      chargeKitRental: false,
    }));
    const hash = this.$route.params.hash.replace(/[^a-z0-9]/gi, "");
    try {
      const { data } = await getJobFromHash(hash);
      this.job = data;
      const { id, name, number, company, notes, market } = data;
      this.submission.market = market;
      this.submission.company = company;
      this.submission.name = name;
      this.submission.number = number;
      this.submission.notes = notes;
      this.jobId = id;
      this.hasEmployeeStartForm = this.job.hasEmployeeStartForm;
      this.submission = JSON.parse(JSON.stringify(this.submission));
    } catch (e) {
      this.$router.push({ name: "404" });
    }
    this.submission.i9.documents.driversLicense.issuingAuthority =
      this.job.market.abbreviation;
    this.unions = getUnions();
    this.$nextTick(() => {
      this.settingUpPage = false;
    });

    // Checks to see if the job submission table is in the local storage dictionary.
    // If it isn't, adds it to the dictionary. If it is, replaces the submission object
    if (!localStorage.getItem(this.job.hash)) {
      localStorage.setItem(this.job.hash, JSON.stringify(this.submission));
    } else {
      const previousSession = JSON.parse(localStorage.getItem(this.job.hash));
      let diff = Object.keys(this.submission).reduce((diff, key) => {
        if (!!previousSession[key] === !!this.submission[key]) return diff;
        return {
          ...diff,
          [key]: this.submission[key],
        };
      }, {});
      this.submission = { ...previousSession, ...diff };
    }

    // Checks to see if the job is non Union, if it is then the union field is set to NON
    if (this.job.isNonUnion) {
      this.submission.employee.union = "NON";
      this.submission.employee.isUnion = false;
    }
  },
  beforeRouteLeave(to, from, next) {
    if (from.name !== "caps" || !this.unsavedDataCreated) {
      next();
      return;
    }
    const answer = window.confirm(
      "Do you really want to leave? you have unsaved changes!"
    );
    if (answer) {
      next();
    } else {
      next(false);
    }
  },

  methods: {
    copyLastDay(dayOfWeek) {
      console.log(dayOfWeek);
      let intDayOfWeekToEdit = 0;
      let daysOfWeekDict = {
        0: "Sunday",
        1: "Monday",
        2: "Tuesday",
        3: "Wednesday",
        4: "Thursday",
        5: "Friday",
        6: "Saturday",
      };
      // Loops throught to find the last day worked before the day the user
      // selected to copy
      let counter = 0;
      let lastDayWorked = [];
      for (const day of this.submission.times) {
        if (day["workedToday"] && dayOfWeek["dayOfWeek"] != day["dayOfWeek"]) {
          lastDayWorked.push(counter);
        } else if (dayOfWeek["dayOfWeek"] == day["dayOfWeek"]) {
          break;
        }
        counter++;
      }

      // Sets the day to edit
      let dayToEdit;
      let lastDayWorkedInt = lastDayWorked[lastDayWorked.length - 1];
      if (lastDayWorked.length != 0) {
        dayToEdit = daysOfWeekDict[lastDayWorkedInt];
      }
      console.log("day to edit = " + dayToEdit);

      // Edits the proper submission.time object the user selected
      for (const day of this.submission.times) {
        if (day["dayOfWeek"] == dayToEdit) {
          dayOfWeek["workedToday"] = true;
          dayOfWeek["zipCode"] = day["zipCode"];
          dayOfWeek["startTime"] = day["startTime"];
          dayOfWeek["endTime"] = day["endTime"];
          dayOfWeek["meals"][0]["startTime"] = day["meals"][0]["startTime"];
          dayOfWeek["meals"][0]["endTime"] = day["meals"][0]["endTime"];
          if (day["recordSecondMeal"]) {
            dayOfWeek["recordSecondMeal"] = true;
            dayOfWeek["meals"][1]["startTime"] = day["meals"][1]["startTime"];
            dayOfWeek["meals"][1]["endTime"] = day["meals"][1]["endTime"];
          }
          dayOfWeek["hasRateChange"] = day["hasRateChange"];
          dayOfWeek["rateChange"] = day["rateChange"];
          dayOfWeek["hasNdb"] = day["hasNdb"];
        }
      }
    },
    getManifest() {
      const manifest = ["caps"];
      if (this.submission.includeStartingPaperwork) {
        if (this.submission.employee.isIncorporated) {
          if (!this.submission.w9.uploadPDF) {
            manifest.push("w9");
          }
        } else {
          manifest.push("w4");
        }
        manifest.push("i9");
      }
      const wageTheft = this.job.payrollProvider.wageThefts.find(
        (wf) => wf.market.key === this.job.market.key
      );
      if (
        (!this.submission.employee.isUnion ||
          this.submission.market.showWageTheftToUnionWorker) &&
        wageTheft &&
        this.submission.includeStartingPaperwork
      ) {
        manifest.push(wageTheft.fileTemplate.key);
      }
      if (this.submission.kitRental.addKitRental) {
        manifest.push("capsKitRental");
      }
      return manifest;
    },
    validate() {
      if (!this.$refs.form.validate()) {
        this.scrollToFirstFormError();
        return false;
      }
      if (this.$refs.signaturePad.isEmpty() && !this.bypassSignature) {
        this.$emit("snackbar", { text: "You must sign first.", color: "red" });
        return false;
      }
      if (
        !this.submission.times.find(
          (time) => time.workedToday || time.covidTest
        ) && !this.job.allowBlankTimecardSubmission
      ) {
        this.$emit("snackbar", {
          text: "You must work (or covid test) at least one day.",
          color: "red",
        });
        return false;
      }
      return true;
    },
    async showPreview() {
      if (!this.validate()) {
        return;
      }
      this.processingPreview = true;
      const { jobId, submission, uploads } = await this.prepareSubmission();
      try {
        const { data } = await generateForm({
          jobId,
          submission,
          uploads,
          timeZone: this.timeZone,
        });
        const objectUrl = URL.createObjectURL(
          new Blob([data], { type: "application/pdf" })
        );
        this.pdfSrc = pdf.createLoadingTask(objectUrl);
        this.pdfSrc.promise.then((_pdf) => {
          this.numPages = _pdf.numPages;
          this.showPreviewDialog = true;
          this.processingPreview = false;
          URL.revokeObjectURL(objectUrl);
        });
      } catch (e) {
        if (e.response.status === 502) {
          this.$emit("snackbar", { text: "502 Bad Gateway", color: "red" });
        } else {
          this.$emit("snackbar", {
            text: "Error Submitting Forms",
            color: "red",
          });
        }
        this.processingPreview = false;
      }
    },
    async submit() {
      if (!this.validate()) {
        return;
      }
      this.sendingToProductionManager = true;
      const { jobId, submission, uploads } = await this.prepareSubmission();
      submission.isPreview = false;
      this.showPreviewDialog = false;
      try {
        await generateForm({
          jobId,
          submission,
          uploads,
          timeZone: this.timeZone,
          artLink: this.artLink,
        });
        this.sendingToProductionManager = false;
        this.unsavedDataCreated = false;

        // Deletes user's data from their local device
        delete localStorage[this.job.hash];

        this.$router.push({
          name: "formComplete",
          params: {
            firstName: submission.employee.firstName,
            lastName: submission.employee.lastName,
            job: this.job,
            freelancerEmail: submission.employee.email,
          },
        });
      } catch (e) {
        if (e.response.status === 502) {
          this.$emit("snackbar", { text: "502 Bad Gateway", color: "red" });
        } else {
          this.$emit("snackbar", {
            text: "Error Submitting Forms",
            color: "red",
          });
        }
        this.sendingToProductionManager = false;
      }
    },
    scrollToFirstFormError() {
      let firstInvalidInput;
      this.$refs.form.inputs.some((input) => {
        if (!input.valid) {
          firstInvalidInput = input;
        }
        return !input.valid;
      });
      if (firstInvalidInput) {
        this.$vuetify.goTo(firstInvalidInput.$el);
      }
    },
    async prepareSubmission() {
      const submission = JSON.parse(JSON.stringify(this.submission));
      if (!this.job.allowBlankTimecardSubmission) {
        const date = DateTime.fromFormat(this.date, "yyyy-MM-dd").toLocal();
        submission.times.forEach((time, index) => {
          time.date = date.plus({ day: index }).toISO();
          time.start = DateTime.fromISO(
            `${date.plus({ day: index }).toISO().split("T")[0]}T${time.startTime}`
          ).toISO();
          time.end = DateTime.fromISO(
            `${date.plus({ day: index }).toISO().split("T")[0]}T${time.endTime}`
          ).toISO();
          time.meals.forEach((meal) => {
            meal.start = DateTime.fromISO(
              `${date.plus({ day: index }).toISO().split("T")[0]}T${
                meal.startTime
              }`
            ).toISO();
            meal.end = DateTime.fromISO(
              `${date.plus({ day: index }).toISO().split("T")[0]}T${meal.endTime}`
            ).toISO();
          });
        });
      }
      if (submission.i9.documentType === "passport") {
        submission.i9.documents.passport.title = "Passport";
        submission.i9.documents.passport.expirationDate = DateTime.fromFormat(
          submission.i9.documents.passport.expirationDate,
          "yyyy-MM-dd"
        ).toLocal();
        submission.i9.documents.A.push(submission.i9.documents.passport);
      } else if (submission.i9.documentType === "driversLicense") {
        submission.i9.documents.driversLicense.title = "Drivers License";
        submission.i9.documents.driversLicense.expirationDate =
          DateTime.fromFormat(
            submission.i9.documents.driversLicense.expirationDate,
            "yyyy-MM-dd"
          ).toLocal();
        submission.i9.documents.B.push(submission.i9.documents.driversLicense);
      } else if (submission.i9.documentType === "other") {
        submission.i9.documents.other.expirationDate = DateTime.fromFormat(
          submission.i9.documents.other.expirationDate,
          "yyyy-MM-dd"
        ).toLocal();
        if (submission.i9.otherDocumentListCategory === "List A") {
          submission.i9.documents.A.push(submission.i9.documents.other);
        } else {
          // List B
          submission.i9.documents.B.push(submission.i9.documents.other);
        }
      }
      submission.i9.documents.C[0] = {
        expires: false,
        title: "Social Security Number",
        issuingAuthority: "Social Security Administration",
        number: submission.employee.socialSecurityNumber,
      };

      if (submission.w9.useSameAddress) {
        submission.w9.address = submission.employee.address;
      }

      submission.employee.birthday = DateTime.fromFormat(
        submission.employee.birthday,
        "yyyy-MM-dd"
      ).toLocal();
      submission.employee.workAuthorization.expirationDate =
        DateTime.fromFormat(
          submission.employee.workAuthorization.expirationDate,
          "yyyy-MM-dd"
        ).toLocal();
      submission.signature = this.$refs.signaturePad.getSignatureImage();
      const uploads = [];
      if (submission.kitRental.fileHash) {
        uploads.push(submission.kitRental.fileHash);
      }
      if (this.w9Upload) {
        uploads.push(this.w9Upload);
      }

      if (this.articleOfIncorporationUpload) {
        uploads.push(this.articleOfIncorporationUpload);
      }

      if (submission.mileageForm.fileHash) {
        uploads.push(submission.mileageForm.fileHash);
      }

      return {
        jobId: this.jobId,
        submission,
        uploads,
      };
    },
    selectUnionWageTheft() {
      if (this.submission.employee.union == "NON") {
        this.submission.employee.isUnion = false;
      }
      else {
        this.submission.employee.isUnion = true;
      }
    }
  },
};
</script>
